import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const ORIGIN = 'https://actyra.co.ke';
const DEFAULT_IMAGE = `${ORIGIN}/og.png`;

const pages = {
  '/': {
    title: 'Actyra | Actuarial Research, Advisory & Valuation Technology',
    description: 'Independent actuarial research, advisory, and valuation technology for African insurers, pension funds, regulators, and risk leaders.',
  },
  '/valuation-engine': {
    title: 'Actuarial Valuation & Pricing Engine | Actyra',
    description: 'Explore Actyra’s Life and General Insurance valuation and pricing tool for product modelling, BEL, IFRS 17, IBNR, Chain Ladder, BF, Cape Cod, UPR, and reporting.',
    breadcrumb: ['Home', 'Valuation Engine'],
  },
  '/contact': {
    title: 'Contact Actyra | Actuarial Research & Advisory Kenya',
    description: 'Contact Actyra in Nairobi for actuarial advisory, research collaboration, valuation technology demonstrations, and insurance risk expertise.',
    breadcrumb: ['Home', 'Contact'],
  },
  '/blogs': {
    title: 'Actuarial Insights & Insurance Perspectives | Actyra',
    description: 'Read practical perspectives from Actyra on actuarial science, insurance risk, capital, solvency, ALM, and African financial services.',
    breadcrumb: ['Home', 'Insights'],
  },
  '/blogs/held-to-maturity-interest-rate-risk': {
    title: 'Held to Maturity, But Not Risk-Free | Actyra',
    description: 'Bonface examines why held-to-maturity bonds still create economic interest rate risk for insurers, and the role of solvency reporting and ALM.',
    type: 'article', author: 'Bonface', published: '2026-08-31', section: 'ALM and Solvency',
    breadcrumb: ['Home', 'Insights', 'Held to Maturity, But Not Risk-Free'],
  },
  '/blogs/risk-management-money-on-the-table': {
    title: 'Poor Risk Management Leaves Money on the Table | Actyra',
    description: 'Explore how African businesses can protect value and make clearer decisions through practical, proactive risk management.',
    type: 'article', author: 'Actyra Advisory Team', published: '2026-06-01', section: 'Risk Management',
    breadcrumb: ['Home', 'Insights', 'Risk Management and Business Value'],
  },
  '/research/financial-reporting': { title: 'Actuarial Financial Reporting Research | Actyra', description: 'Actyra research on IFRS 17, actuarial financial reporting, measurement, disclosures, and insurance reporting practice in Africa.', breadcrumb: ['Home', 'Research', 'Financial Reporting'] },
  '/research/solvency-capital': { title: 'Solvency & Capital Research for Insurers | Actyra', description: 'Independent actuarial research on insurance solvency, regulatory capital, stress testing, and risk-based supervision across Africa.', breadcrumb: ['Home', 'Research', 'Solvency and Capital'] },
  '/research/mortality-morbidity': { title: 'Mortality & Morbidity Research | Actyra', description: 'Actuarial research and insight on mortality, morbidity, experience analysis, and demographic risk for African insurers.', breadcrumb: ['Home', 'Research', 'Mortality and Morbidity'] },
  '/research/asset-liability': { title: 'Asset-Liability Management Research | Actyra', description: 'Actyra perspectives on insurance ALM, duration mismatch, investment risk, interest rate risk, and long-term liability portfolios.', breadcrumb: ['Home', 'Research', 'Asset-Liability Management'] },
  '/research/innovation-risk': { title: 'Insurance Innovation & Emerging Risk Research | Actyra', description: 'Research on emerging insurance risks, actuarial innovation, technology, climate, and changing African risk landscapes.', breadcrumb: ['Home', 'Research', 'Innovation and Emerging Risk'] },
  '/games': { title: 'Actuarial & Insurance Learning Games | Actyra', description: 'Explore interactive Actyra games that make insurance, risk, and actuarial concepts practical and engaging.', breadcrumb: ['Home', 'Games'] },
  '/games/bimapath': { title: 'Bima Path | Kenyan Life Insurance Simulator', description: 'Navigate a 25-year Kenyan life insurance journey and explore how products, premiums, claims, and life events affect financial outcomes.', breadcrumb: ['Home', 'Games', 'Bima Path'] },
  '/privacy': { title: 'Privacy Policy | Actyra', description: 'Read how Actyra handles personal data and protects the privacy of website visitors and contacts.', breadcrumb: ['Home', 'Privacy Policy'] },
  '/terms': { title: 'Terms of Use | Actyra', description: 'Terms governing the use of the Actyra website, research, tools, and published content.', breadcrumb: ['Home', 'Terms of Use'] },
  '/data-usage': { title: 'Data Usage Policy | Actyra', description: 'Learn how Actyra uses website and contact data, including its approach to tracking, advertising, and third-party sharing.', breadcrumb: ['Home', 'Data Usage Policy'] },
};

const setMeta = (selector, attribute, value) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    const match = selector.match(/meta\[(name|property)="([^"]+)"\]/);
    if (match) element.setAttribute(match[1], match[2]);
    document.head.appendChild(element);
  }
  element.setAttribute(attribute, value);
};

const organization = {
  '@context': 'https://schema.org', '@type': 'Organization',
  name: 'Actyra', url: ORIGIN, logo: `${ORIGIN}/Main%20Logo.png`,
  email: 'info@actyra.co.ke', telephone: '+254740095045',
  address: { '@type': 'PostalAddress', addressLocality: 'Nairobi', addressCountry: 'KE' },
  sameAs: [
    'https://www.linkedin.com/company/actyr%C7%8E/',
    'https://x.com/actyralimited',
    'https://www.instagram.com/actyralimited/',
    'https://web.facebook.com/profile.php?id=61577411862890',
  ],
};

export default function SEOManager() {
  const { pathname } = useLocation();

  useEffect(() => {
    const page = pages[pathname] || {
      title: 'Actyra | Clarity in Risk and Capital',
      description: 'Independent actuarial research, advisory, and risk insight for African financial services.',
    };
    const canonical = `${ORIGIN}${pathname === '/' ? '/' : pathname}`;
    document.title = page.title;
    setMeta('meta[name="description"]', 'content', page.description);
    setMeta('meta[name="robots"]', 'content', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    setMeta('meta[property="og:title"]', 'content', page.title);
    setMeta('meta[property="og:description"]', 'content', page.description);
    setMeta('meta[property="og:type"]', 'content', page.type === 'article' ? 'article' : 'website');
    setMeta('meta[property="og:url"]', 'content', canonical);
    setMeta('meta[property="og:image"]', 'content', DEFAULT_IMAGE);
    setMeta('meta[name="twitter:title"]', 'content', page.title);
    setMeta('meta[name="twitter:description"]', 'content', page.description);
    setMeta('meta[name="twitter:image"]', 'content', DEFAULT_IMAGE);

    let canonicalLink = document.head.querySelector('link[rel="canonical"]');
    if (!canonicalLink) { canonicalLink = document.createElement('link'); canonicalLink.rel = 'canonical'; document.head.appendChild(canonicalLink); }
    canonicalLink.href = canonical;

    document.head.querySelectorAll('script[data-actyra-seo]').forEach((node) => node.remove());
    const graphs = pathname === '/' ? [organization, { '@context': 'https://schema.org', '@type': 'WebSite', name: 'Actyra', alternateName: 'Actyra Limited', url: ORIGIN }] : [];
    if (page.breadcrumb) graphs.push({ '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: page.breadcrumb.map((name, index) => ({ '@type': 'ListItem', position: index + 1, name, item: index === page.breadcrumb.length - 1 ? canonical : `${ORIGIN}${index === 0 ? '/' : pathname.split('/').slice(0, index + 1).join('/')}` })) });
    if (page.type === 'article') graphs.push({ '@context': 'https://schema.org', '@type': 'Article', headline: page.title.replace(' | Actyra', ''), description: page.description, datePublished: page.published, dateModified: page.published, author: { '@type': page.author === 'Bonface' ? 'Person' : 'Organization', name: page.author }, publisher: { '@type': 'Organization', name: 'Actyra', logo: { '@type': 'ImageObject', url: `${ORIGIN}/Main%20Logo.png` } }, mainEntityOfPage: canonical, articleSection: page.section, image: DEFAULT_IMAGE });
    graphs.forEach((graph) => { const script = document.createElement('script'); script.type = 'application/ld+json'; script.dataset.actyraSeo = 'true'; script.text = JSON.stringify(graph); document.head.appendChild(script); });
  }, [pathname]);

  return null;
}
